import {
	createStyles,
	makeStyles,
	Box,
	List as MUIList,
	ListItem,
	ListItemSecondaryAction,
	Checkbox,
	ListItemAvatar,
	Avatar,
	Button,
	Divider,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { Waypoint } from 'react-waypoint';

export type ListProps<T> = {
	primaryKey: keyof T;
	renderListItemText: (element: T) => React.ReactElement;
	avatar?: React.ReactChild | null;
	infiniteScroll?: {
		endOfList: boolean;
		onEndReached: () => any;
	} | null;
	selected?: {
		elements: T[];
		handleListClick: (element: T) => any;
	} | null;
};

type Props<T> = ListProps<T> & {
	loading?: boolean;
	elements: T[];
};

function List<T>({
	elements,
	primaryKey: key,
	loading = false,
	selected,
	infiniteScroll,
	avatar,
	renderListItemText,
}: Props<T>) {
	const { t } = useTranslation();

	const { elements: selectedElements, handleListClick: onClick } = selected || {
		elements: [],
		handleListClick: null,
		primaryKey: '' as keyof T,
	};
	const { endOfList, onEndReached } = infiniteScroll || { endOfList: true, onEndReached: null };

	const classes = useStyles();

	return (
		<Box className={classes.fullHeight}>
			<MUIList>
				{elements.map((element) => {
					const itemKey = `table-${element[key]}`;
					return (
						<Box>
							<ListItem key={itemKey} button>
								{!!avatar && (
									<ListItemAvatar>
										<Avatar>{avatar}</Avatar>
									</ListItemAvatar>
								)}
								{renderListItemText(element)}
								<ListItemSecondaryAction>
									{!!onClick && (
										<Checkbox
											edge="end"
											onChange={() => onClick(element)}
											checked={selectedElements.find((el) => el[key] === element[key]) ? true : false}
										/>
									)}
								</ListItemSecondaryAction>
							</ListItem>
							<Divider />
						</Box>
					);
				})}
				{!!onEndReached && !endOfList && <Waypoint onEnter={onEndReached} />}
				{!!onEndReached && !loading && !endOfList && (
					<ListItem button>
						<ListItemSecondaryAction>
							<Button onClick={onEndReached}>{t('common:more')}</Button>
						</ListItemSecondaryAction>
					</ListItem>
				)}
			</MUIList>
		</Box>
	);
}

const useStyles = makeStyles(() =>
	createStyles({
		fullHeight: {
			overflow: 'auto',
		},
	})
);

export default List;
